/*
  KS Cart Cross-Sell. Loads the complementary products paired to every product
  in the cart and renders them in the cart drawer and on the cart page. Pairings
  are managed by the merchant in Shopify's Search & Discovery app. Prestige owns
  cart mutations, product forms, and section replacement.
*/

/* Recommendations are requested per cart product, so cap the fan-out. */
const KS_MAX_SOURCE_PRODUCTS = 6;

/* Shopify serves up to 10 complementary products per source product. */
const KS_PAIRINGS_PER_PRODUCT = 10;

const KS_SECTION_ID = "ks-cart-cross-sell";

/*
  Normalizes numeric and GraphQL resource IDs for reliable comparisons.
*/
/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeProductId(value) {
  return String(value ?? "").split("/").pop()?.trim() || "";
}

/*
  Reads the comma-separated product ids rendered into the placeholder.
*/
/**
 * @param {unknown} value
 * @returns {string[]}
 */
function parseProductIds(value) {
  return String(value || "")
    .split(",")
    .map((id) => normalizeProductId(id))
    .filter(Boolean);
}

/*
  Collects the unique product ids of an Ajax cart payload.
*/
/**
 * @param {{ items?: { product_id?: string | number }[] } | undefined} cart
 * @returns {string[]}
 */
function getCartProductIds(cart) {
  if (!cart || !Array.isArray(cart.items)) return [];

  const productIds = cart.items
    .map((item) => normalizeProductId(item?.product_id))
    .filter(Boolean);

  return [...new Set(productIds)];
}

/*
  Requests one product's complementary pairings and returns the rendered cards.
*/
/**
 * @param {string} productId
 * @param {AbortSignal} signal
 * @returns {Promise<HTMLElement[]>}
 */
async function fetchPairings(productId, signal) {
  const root = window.Shopify?.routes?.root || "/";
  const url = new URL(`${root}recommendations/products`, window.location.origin);

  url.searchParams.set("section_id", KS_SECTION_ID);
  url.searchParams.set("product_id", productId);
  url.searchParams.set("limit", String(KS_PAIRINGS_PER_PRODUCT));
  url.searchParams.set("intent", "complementary");

  const response = await fetch(url.toString(), { signal });

  /*
    Products that are not available to the Online Store channel answer 404.
    That simply means no pairings, so it is not treated as a failure.
  */
  if (!response.ok) return [];

  const markup = new DOMParser().parseFromString(
    await response.text(),
    "text/html",
  );

  return /** @type {HTMLElement[]} */ (
    Array.from(markup.querySelectorAll("[data-ks-cross-sell-product-id]"))
  );
}

/*
  Interleaves each cart product's pairings so every cart item gets a turn,
  skipping duplicates and anything already in the cart.
*/
/**
 * @param {HTMLElement[][]} groups
 * @param {string[]} excludedProductIds
 * @param {number} limit
 * @returns {HTMLElement[]}
 */
function mergePairings(groups, excludedProductIds, limit) {
  /** @type {HTMLElement[]} */
  const merged = [];
  const seen = new Set(excludedProductIds);
  const depth = Math.max(0, ...groups.map((group) => group.length));

  for (let index = 0; index < depth; index += 1) {
    for (const group of groups) {
      const item = group[index];
      if (!item) continue;

      const productId = normalizeProductId(item.dataset.ksCrossSellProductId);
      if (!productId || seen.has(productId)) continue;

      seen.add(productId);
      merged.push(item);

      if (merged.length >= limit) return merged;
    }
  }

  return merged;
}

/*
  Prefixes element ids so the quick-buy modals of the drawer and the cart page
  never collide when both placements render the same product.
*/
/**
 * @param {HTMLElement} item
 * @param {string} prefix
 */
function scopeElementIds(item, prefix) {
  item.querySelectorAll("[id]").forEach((node) => {
    const originalId = node.id;
    const scopedId = `${prefix}-${originalId}`;

    item
      .querySelectorAll(`[aria-controls="${CSS.escape(originalId)}"]`)
      .forEach((control) => control.setAttribute("aria-controls", scopedId));

    node.id = scopedId;
  });
}

/* Reconnect automatically when Prestige replaces the cart markup. */
class KsCartCrossSell extends HTMLElement {
  connectedCallback() {
    this.ksController?.abort();
    this.ksController = new AbortController();
    const { signal } = this.ksController;

    this.ksList = this.querySelector("[data-ks-cross-sell-list]");
    this.ksLimit = Number(this.dataset.ksLimit) || 3;

    this.ksLoad(parseProductIds(this.dataset.ksCartProductIds));

    document.addEventListener(
      "cart:change",
      (event) => this.ksLoad(getCartProductIds(event.detail?.cart)),
      { signal },
    );

    document.addEventListener("cart:refresh", () => this.ksRefresh(), {
      signal,
    });
  }

  disconnectedCallback() {
    this.ksController?.abort();
    this.ksRequest?.abort();
  }

  /*
    Reloads the pairings from the live cart when Prestige cannot supply one.
  */
  async ksRefresh() {
    try {
      const response = await fetch(`${Shopify.routes.root}cart.js`);
      if (!response.ok) throw new Error("Cart refresh failed");

      this.ksLoad(getCartProductIds(await response.json()));
    } catch (error) {
      console.warn("[KS Cart Cross-Sell]", error);
    }
  }

  /*
    Requests the pairings of every cart product, then renders the merged list.
  */
  /**
   * @param {string[]} cartProductIds
   */
  async ksLoad(cartProductIds) {
    const sourceProductIds = cartProductIds.slice(0, KS_MAX_SOURCE_PRODUCTS);
    const signature = sourceProductIds.join(",");
    if (signature === this.ksSignature) return;

    this.ksSignature = signature;
    this.ksRequest?.abort();

    if (sourceProductIds.length === 0) {
      this.ksRender([]);
      return;
    }

    this.ksRequest = new AbortController();
    const { signal } = this.ksRequest;

    try {
      const groups = await Promise.all(
        sourceProductIds.map((productId) =>
          fetchPairings(productId, signal).catch((error) => {
            if (error.name === "AbortError") throw error;

            console.warn("[KS Cart Cross-Sell]", error);
            return [];
          }),
        ),
      );

      this.ksRender(mergePairings(groups, cartProductIds, this.ksLimit));
    } catch (error) {
      if (error.name === "AbortError") return;

      this.ksSignature = undefined;
      console.warn("[KS Cart Cross-Sell]", error);
    }
  }

  /*
    Swaps in the paired products and hides the region when there are none.
  */
  /**
   * @param {HTMLElement[]} items
   */
  ksRender(items) {
    if (!this.ksList) return;

    items.forEach((item) => scopeElementIds(item, this.id));
    this.ksList.replaceChildren(...items);
    this.hidden = items.length === 0;
  }
}

if (!customElements.get("ks-cart-cross-sell")) {
  customElements.define("ks-cart-cross-sell", KsCartCrossSell);
}
